import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { LibraryAlias, CreateLibraryAliasRequest, UpdateLibraryAliasRequest } from '@/types/api';
import { LibraryAliasType } from '@/types/enums';
import { formatDate } from '@/utils/dateUtils';

interface AliasListProps {
  open: boolean;
  libraryName: string;
  aliases: LibraryAlias[];
  onClose: () => void;
  onAddAlias: (data: CreateLibraryAliasRequest) => void;
  onUpdateAlias: (aliasId: number, data: UpdateLibraryAliasRequest) => void;
  onDeleteAlias: (aliasId: number) => void;
  isLoading?: boolean;
}

const AliasList: React.FC<AliasListProps> = ({
  open,
  libraryName,
  aliases,
  onClose,
  onAddAlias,
  onUpdateAlias,
  onDeleteAlias,
  isLoading = false,
}) => {
  const { t } = useTranslation('components');
  const [aliasText, setAliasText] = useState('');
  const [aliasType, setAliasType] = useState<LibraryAliasType>(LibraryAliasType.TEXT);
  const [editingAlias, setEditingAlias] = useState<LibraryAlias | null>(null);

  const handleAddAlias = () => {
    if (!aliasText.trim()) {
      alert(t('aliasList.addAlias.alertNoAliasText'));
      return;
    }

    onAddAlias({
      alias: aliasText.trim(),
      alias_type: aliasType,
    });

    // Reset form
    setAliasText('');
    setAliasType(LibraryAliasType.TEXT);
  };

  const handleUpdateAlias = (aliasId: number, data: UpdateLibraryAliasRequest) => {
    onUpdateAlias(aliasId, data);
    setEditingAlias(null);
  };

  const getAliasTypeColor = (type: LibraryAliasType) => {
    switch (type) {
      case LibraryAliasType.TEXT:
        return 'default';
      case LibraryAliasType.HASHTAG:
        return 'primary';
      case LibraryAliasType.SECTION:
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('aliasList.title')}
        <Typography variant="body2" color="text.secondary" sx={{ direction: 'rtl', textAlign: 'right', mt: 0.5 }}>
          {libraryName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Add Alias Form */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('aliasList.addAlias.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              label={t('aliasList.addAlias.aliasTextLabel')}
              size="small"
              fullWidth
              value={aliasText}
              onChange={(e) => setAliasText(e.target.value)}
              disabled={isLoading}
              inputProps={{ dir: 'rtl' }}
              placeholder={t('aliasList.addAlias.aliasTextPlaceholder')}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('aliasList.addAlias.aliasTypeLabel')}</InputLabel>
              <Select
                value={aliasType}
                onChange={(e) => setAliasType(e.target.value as LibraryAliasType)}
                disabled={isLoading}
                label={t('aliasList.addAlias.aliasTypeLabel')}
              >
                <MenuItem value={LibraryAliasType.TEXT}>{t('aliasList.addAlias.aliasTypeText')}</MenuItem>
                <MenuItem value={LibraryAliasType.HASHTAG}>{t('aliasList.addAlias.aliasTypeHashtag')}</MenuItem>
                <MenuItem value={LibraryAliasType.SECTION}>{t('aliasList.addAlias.aliasTypeSection')}</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddAlias}
              disabled={isLoading || !aliasText.trim()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('aliasList.addAlias.addButton')}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary">
            • {t('aliasList.addAlias.helperText1')}
            <br />
            • {t('aliasList.addAlias.helperText2')}
            <br />
            • {t('aliasList.addAlias.helperText3')}
          </Typography>
        </Box>

        {/* Aliases List */}
        <Typography variant="subtitle2" gutterBottom>
          {t('aliasList.currentAliases.title', { count: aliases.length })}
        </Typography>

        {aliases.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            {t('aliasList.currentAliases.emptyState')}
          </Typography>
        ) : (
          <List>
            {aliases.map((alias) => (
              <ListItem key={alias.id} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ direction: 'rtl' }}>
                        {alias.alias}
                      </Typography>
                      <Chip
                        label={alias.alias_type}
                        size="small"
                        color={getAliasTypeColor(alias.alias_type)}
                      />
                    </Box>
                  }
                  secondary={t('aliasList.currentAliases.added', { date: formatDate(alias.created_at, 'PP') })}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => setEditingAlias(alias)}
                    disabled={isLoading}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => onDeleteAlias(alias.id)}
                    disabled={isLoading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          {t('aliasList.actions.close')}
        </Button>
      </DialogActions>

      {/* Edit Alias Dialog */}
      {editingAlias && (
        <AliasEditDialog
          open={!!editingAlias}
          alias={editingAlias}
          onClose={() => setEditingAlias(null)}
          onUpdate={handleUpdateAlias}
          isLoading={isLoading}
        />
      )}
    </Dialog>
  );
};

// ============================================================================
// Edit Alias Dialog (TODO: Move to its own component file)
// ============================================================================

interface AliasEditDialogProps {
  open: boolean;
  alias: LibraryAlias;
  onClose: () => void;
  onUpdate: (aliasId: number, data: UpdateLibraryAliasRequest) => void;
  isLoading?: boolean;
}

const AliasEditDialog: React.FC<AliasEditDialogProps> = ({
  open,
  alias,
  onClose,
  onUpdate,
  isLoading = false,
}) => {
  const { t } = useTranslation('components');
  const [aliasText, setAliasText] = useState(alias.alias);
  const [aliasType, setAliasType] = useState<LibraryAliasType>(alias.alias_type);

  const handleSave = () => {
    if (!aliasText.trim()) {
      alert(t('aliasEditDialog.alertNoAliasText'));
      return;
    }

    onUpdate(alias.id, {
      alias: aliasText.trim(),
      alias_type: aliasType,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('aliasEditDialog.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('aliasEditDialog.aliasTextLabel')}
            fullWidth
            value={aliasText}
            onChange={(e) => setAliasText(e.target.value)}
            disabled={isLoading}
            inputProps={{ dir: 'rtl' }}
          />
          <FormControl fullWidth>
            <InputLabel>{t('aliasEditDialog.aliasTypeLabel')}</InputLabel>
            <Select
              value={aliasType}
              onChange={(e) => setAliasType(e.target.value as LibraryAliasType)}
              disabled={isLoading}
              label={t('aliasEditDialog.aliasTypeLabel')}
            >
              <MenuItem value={LibraryAliasType.TEXT}>{t('aliasEditDialog.aliasTypeText')}</MenuItem>
              <MenuItem value={LibraryAliasType.HASHTAG}>{t('aliasEditDialog.aliasTypeHashtag')}</MenuItem>
              <MenuItem value={LibraryAliasType.SECTION}>{t('aliasEditDialog.aliasTypeSection')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t('aliasEditDialog.actions.cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
          {t('aliasEditDialog.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AliasList;
